import React, { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { X } from "lucide-react";
import SearchableSelect from "../ui/SearchableSelect";
import { timeEntriesApi } from "../../services/api";
import { useUserDataContext } from "../../context/UserDataContext";
import { formatDuration, minutesToTimeStr } from "./timeCalendarUtils";

// ── Helpers ──────────────────────────────────────────────────────────────────

function formatTimeLabel(timeStr: string): string {
  const [h, m] = timeStr.split(":").map(Number);
  const ampm = h >= 12 ? "PM" : "AM";
  const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return `${h12}:${String(m).padStart(2, "0")} ${ampm}`;
}

function calculateEndTime(startTime: string, minutes: number): string {
  const [h, m] = startTime.split(":").map(Number);
  const totalMinutes = h * 60 + m + minutes;
  const endH = Math.floor(totalMinutes / 60) % 24;
  const endM = totalMinutes % 60;
  return `${String(endH).padStart(2, "0")}:${String(endM).padStart(2, "0")}`;
}

// ── Props ────────────────────────────────────────────────────────────────────

interface Props {
  date: string;
  startTime: string;
  minutes: number;
  anchorX: number;
  anchorY: number;
  matters: any[];
  clients: any[];
  onClose: () => void;
  onCreated: () => void;
}

// ── Component ────────────────────────────────────────────────────────────────

const TimeCalendarQuickCreate: React.FC<Props> = ({
  date,
  startTime,
  minutes,
  anchorX,
  anchorY,
  matters,
  clients,
  onClose,
  onCreated,
}) => {
  const { userData } = useUserDataContext();
  const popoverRef = useRef<HTMLDivElement>(null);

  const [matterId, setMatterId] = useState("");
  const [narrative, setNarrative] = useState("");
  const [rateCents, setRateCents] = useState(0);
  const [billable, setBillable] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const endTime = calculateEndTime(startTime, minutes);

  // ── Smart positioning ────────────────────────────────────────────────────

  const [position, setPosition] = useState<React.CSSProperties>({});

  useEffect(() => {
    const POPOVER_WIDTH = 320;
    const POPOVER_HEIGHT = 340;
    const MARGIN = 12;

    let left = anchorX + MARGIN;
    let top = anchorY;

    // If too close to right edge, position left of anchor
    if (anchorX + POPOVER_WIDTH + MARGIN > window.innerWidth) {
      left = anchorX - POPOVER_WIDTH - MARGIN;
    }

    // If too close to bottom, position above anchor
    if (anchorY + POPOVER_HEIGHT > window.innerHeight) {
      top = anchorY - POPOVER_HEIGHT;
    }

    // Clamp to viewport
    left = Math.max(MARGIN, Math.min(left, window.innerWidth - POPOVER_WIDTH - MARGIN));
    top = Math.max(MARGIN, Math.min(top, window.innerHeight - POPOVER_HEIGHT - MARGIN));

    setPosition({ left, top });
  }, [anchorX, anchorY]);

  // ── Click outside to close ───────────────────────────────────────────────

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        popoverRef.current &&
        !popoverRef.current.contains(e.target as Node)
      ) {
        onClose();
      }
    };
    // Delay listener attachment so the creating click doesn't immediately close
    const timer = setTimeout(() => {
      document.addEventListener("mousedown", handleClickOutside);
    }, 0);
    return () => {
      clearTimeout(timer);
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [onClose]);

  // ── Auto-fill rate from matter ───────────────────────────────────────────

  const handleMatterChange = (id: string | null) => {
    setMatterId(id || "");
    if (id) {
      const matter = matters.find((m: any) => m._id === id);
      if (matter?.rateCents) {
        setRateCents(matter.rateCents);
      } else if (matter?.defaultRateCents) {
        setRateCents(matter.defaultRateCents);
      }
    }
  };

  // ── Derive clientId from matter ──────────────────────────────────────────

  const getClientId = (): string => {
    const matter = matters.find((m: any) => m._id === matterId);
    if (!matter) return "";
    return typeof matter.clientId === "object"
      ? matter.clientId._id
      : matter.clientId || "";
  };

  // ── Save ─────────────────────────────────────────────────────────────────

  const handleSave = async () => {
    if (!matterId || !narrative.trim()) return;

    setSaving(true);
    setError("");

    try {
      await timeEntriesApi.create({
        matterId,
        clientId: getClientId(),
        userId: userData._id,
        date,
        startTime,
        minutes,
        narrative: narrative.trim(),
        billable,
        rateCents,
      });
      onCreated();
    } catch (err: any) {
      setError(
        err.response?.data?.error ||
          err.response?.data?.message ||
          "Failed to save time entry"
      );
    } finally {
      setSaving(false);
    }
  };

  // ── Matter options for SearchableSelect ──────────────────────────────────

  const matterOptions = matters.map((m: any) => ({
    value: m._id,
    label: `${m.matterNumber} - ${m.title}`,
  }));

  return (
    <motion.div
      ref={popoverRef}
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.95 }}
      transition={{ duration: 0.15, ease: "easeOut" }}
      style={{
        position: "fixed",
        ...position,
        width: 320,
        zIndex: 50,
      }}
      className="bg-white rounded-xl shadow-xl border border-slate-200"
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 pt-3 pb-2 border-b border-slate-100">
        <h3 className="text-sm font-semibold text-slate-800">New Time Entry</h3>
        <button
          onClick={onClose}
          className="p-1 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      <div className="p-4 space-y-3">
        {/* Matter */}
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">
            Matter *
          </label>
          <SearchableSelect
            options={matterOptions}
            value={matterId || null}
            onChange={handleMatterChange}
            placeholder="Select matter..."
            required
          />
        </div>

        {/* Narrative */}
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">
            Narrative *
          </label>
          <textarea
            value={narrative}
            onChange={(e) => setNarrative(e.target.value)}
            rows={2}
            placeholder="Describe the work performed..."
            className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:border-emerald-600 resize-none"
          />
        </div>

        {/* Time display */}
        <div className="flex items-center gap-2 text-sm text-slate-600">
          <span className="font-medium">{formatTimeLabel(startTime)}</span>
          <span className="text-slate-400">-</span>
          <span className="font-medium">{formatTimeLabel(endTime)}</span>
          <span className="text-slate-400 ml-1">({formatDuration(minutes)})</span>
        </div>

        {/* Rate + Billable */}
        <div className="flex items-center gap-3">
          <div className="flex-1">
            <label className="block text-xs font-medium text-slate-600 mb-1">
              Rate ($/hr)
            </label>
            <input
              type="number"
              min="0"
              step="0.01"
              value={rateCents ? rateCents / 100 : ""}
              onChange={(e) =>
                setRateCents(Math.round(parseFloat(e.target.value || "0") * 100))
              }
              placeholder="250.00"
              className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:border-emerald-600"
            />
          </div>
          <div className="flex items-center gap-2 pt-5">
            <input
              type="checkbox"
              id="qc-billable"
              checked={billable}
              onChange={(e) => setBillable(e.target.checked)}
              className="h-4 w-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
            />
            <label
              htmlFor="qc-billable"
              className="text-sm font-medium text-slate-700"
            >
              Billable
            </label>
          </div>
        </div>

        {/* Error message */}
        {error && (
          <p className="text-xs text-red-600 bg-red-50 rounded-lg px-3 py-2">
            {error}
          </p>
        )}

        {/* Actions */}
        <div className="flex items-center justify-end gap-2 pt-1">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-xl border border-slate-200 text-sm font-medium text-slate-600 hover:bg-slate-50 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving || !matterId || !narrative.trim()}
            className="px-4 py-2 rounded-xl text-white text-sm font-bold transition-colors disabled:opacity-50"
            style={{ backgroundColor: "#0F5132" }}
            onMouseEnter={(e) => {
              if (!saving) e.currentTarget.style.backgroundColor = "#146C43";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = "#0F5132";
            }}
          >
            {saving ? "Saving..." : "Save"}
          </button>
        </div>
      </div>
    </motion.div>
  );
};

export default TimeCalendarQuickCreate;
